import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "../../utils/supabaseClient";

export default function StudentSidebar({ onSectionChange, activeSection, studentData, newHomeworkCount = 0 }) {
  const [tooltip, setTooltip] = useState({ show: false, text: "", top: 0 });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const sidebarRef = useRef();
  const navigate = useNavigate();

  // Get student data
  const getStudentInfo = () => {
    if (studentData) {
      // Приоритет: email из studentData (загружается из Supabase Auth)
      // Затем contactEmail, затем userAccount.email (для совместимости)
      const email = studentData.email || studentData.contactEmail || 
                    (studentData.userAccount?.email) || 
                    'student@example.com';
      
      return {
        name: studentData.name || 'Student',
        email: email,
        initials: (studentData.name || 'Student').split(' ').map(n => n[0]).join('').toUpperCase() || 'S'
      };
    }
    
    // Fallback к данным из localStorage или Supabase Auth
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    return {
      name: user.name || 'Student',
      email: user.email || 'student@example.com',
      initials: (user.name || 'Student').split(' ').map(n => n[0]).join('').toUpperCase() || 'S'
    };
  };

  const studentInfo = getStudentInfo();
  const userName = studentInfo.name;
  const userEmail = studentInfo.email;
  const userInitials = studentInfo.initials;

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuOpen && !event.target.closest('.user-menu-container')) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  const studentLinks = [
    {
      name: "Home",
      path: "/student-dashboard",
      icon: (
        <svg
          width="24"
          height="24"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m4 12 8-8 8 8M6 10.5V19a1 1 0 0 0 1 1h3v-3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3h3a1 1 0 0 0 1-1v-8.5"
          />
        </svg>
      ),
    },

    {
      name: "Dictionary",
      path: "/student-dictionary",
      icon: (
        <svg width="24" height="24" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
    },
    {
      name: "Homework",
      path: "/student-homework",
      icon: (
        <svg width="24" height="24" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z"
          />
        </svg>
      ),
    },
    {

      icon: (
        <svg width="24" height="24" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2Zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2Z"
          />
        </svg>
      ),
    },
  ];

  const handleSectionClick = (sectionName) => {
    // Проверяем, открыт ли урок с homework, чтобы показать модальное окно сохранения
    const currentPath = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    const isInLesson = currentPath.includes('/lesson/');
    const fromHomework = urlParams.get('fromHomework') === 'true';
    const homeworkId = urlParams.get('homeworkId');
    
    if (isInLesson && (fromHomework || homeworkId)) {
      // Отправляем событие для показа модального окна сохранения
      const event = new CustomEvent('checkUnsavedChanges', {
        detail: {
          targetSection: sectionName,
          navigate: () => {
            onSectionChange(sectionName);
            if (sectionName === 'home') {
              const pathParts = currentPath.split('/');
              const studentSlugIndex = pathParts.findIndex(part => part === 'student-dashboard') + 1;
              const studentSlug = pathParts[studentSlugIndex];
              if (studentSlug) {
                navigate(`/student-dashboard/${studentSlug}`);
              }
            } else {
              const pathParts = currentPath.split('/');
              const studentSlugIndex = pathParts.findIndex(part => part === 'student-dashboard') + 1;
              const studentSlug = pathParts[studentSlugIndex];
              if (studentSlug) {
                navigate(`/student-dashboard/${studentSlug}?section=${sectionName}`);
              }
            }
          }
        }
      });
      window.dispatchEvent(event);
    } else {
      // Обычная навигация без модального окна
      onSectionChange(sectionName);
      
      if (sectionName === 'home') {
        if (currentPath.includes('/lesson/')) {
          const pathParts = currentPath.split('/');
          const studentSlugIndex = pathParts.findIndex(part => part === 'student-dashboard') + 1;
          const studentSlug = pathParts[studentSlugIndex];
          if (studentSlug) {
            navigate(`/student-dashboard/${studentSlug}`);
          }
        }
      } else {
        const pathParts = currentPath.split('/');
        const studentSlugIndex = pathParts.findIndex(part => part === 'student-dashboard') + 1;
        const studentSlug = pathParts[studentSlugIndex];
        if (studentSlug && currentPath.includes('/lesson/')) {
          navigate(`/student-dashboard/${studentSlug}?section=${sectionName}`);
        }
      }
    }
  };

  const handleMouseEnter = (e, text) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      show: true,
      text,
      top: rect.top + rect.height / 2,
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, text: "", top: 0 });
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  const handleLogout = async () => {
    try {
      // Выходим из Supabase Auth
      await signOut();
    } catch (error) {
      console.error('Error signing out from Supabase:', error);
      // Продолжаем выход даже если была ошибка
    }
    
    // Очищаем localStorage
    localStorage.removeItem('currentUser');
    localStorage.removeItem('isAuthenticated');
    setUserMenuOpen(false);
    
    // Редирект на страницу входа
    navigate('/login');
    window.location.reload(); // Перезагружаем страницу для полной очистки состояния
  };

  return (
    <>
      <div className="student-sidebar" ref={sidebarRef}>
        <nav className="student-sidebar-nav">
          {studentLinks.filter(link => link.name).map(link => (
            <div
              key={link.name}
              data-tour={`student-nav-${link.name.toLowerCase()}`}
              className={`student-sidebar-link ${activeSection === link.name.toLowerCase() ? 'active' : ''}`}
              onClick={() => handleSectionClick(link.name.toLowerCase())}
              onMouseEnter={e => link.name && handleMouseEnter(e, link.name)}
              onMouseLeave={handleMouseLeave}
            >
              <span className="student-sidebar-icon-wrap">
                <span className="student-sidebar-svg-icon">{link.icon}</span>
                {link.name === 'Homework' && newHomeworkCount > 0 && (
                  <span className="student-sidebar-homework-badge" aria-label={`${newHomeworkCount} new homework`}>
                    {newHomeworkCount > 9 ? '9+' : newHomeworkCount}
                  </span>
                )}
              </span>
            </div>
          ))}
          
          {/* User Profile Icon at the very bottom */}
          <div
            className="student-sidebar-link profile-icon-container"
            data-tour="student-nav-profile"
            style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "center", padding: "8px", borderRadius: "50%", width: "48px", height: "48px" }}
            onClick={toggleUserMenu}
            onMouseEnter={e => handleMouseEnter(e, "User Profile")}
            onMouseLeave={handleMouseLeave}
          >
            <div style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              background: "radial-gradient(circle at 70% 30%, #ff7b1c 0%, #e6420b 40%, #1a1a1a 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              cursor: "pointer",
              border: "1px solid gray",
              flexShrink: 0
            }}>
              <span style={{
                color: "white",
                fontWeight: 600,
                fontSize: "12px",
                letterSpacing: "0.5px",
                textTransform: "uppercase"
              }}>{userInitials}</span>
            </div>
          </div>
        </nav>
      </div>
      
      {/* User Menu */}
      {userMenuOpen && (
        <div className="user-menu-container" style={{
          position: "fixed",
          left: "100px",
          bottom: "270px",
          background: "white",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          padding: "8px 0",
          zIndex: 1500,
          minWidth: "200px"
        }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #eee" }}>
            <div style={{ fontWeight: "600", fontSize: "14px" }}>{userName}</div>
            <div style={{ fontSize: "12px", color: "#666" }}>{userEmail}</div>
          </div>
          <div style={{ padding: "4px 0" }}>
            <button style={{
              width: "100%",
              background: "none",
              border: "none",
              padding: "8px 16px",
              textAlign: "left",
              cursor: "pointer",
              fontSize: "14px",
              transition: "background-color 0.2s ease"
            }} 
            onMouseEnter={(e) => e.target.style.backgroundColor = "#f8f9fa"}
            onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
            onClick={() => {
              setUserMenuOpen(false);
              onSectionChange('profile');
            }}>Profile Settings</button>
            <button style={{
              width: "100%",
              background: "none",
              border: "none",
              padding: "8px 16px",
              textAlign: "left",
              cursor: "pointer",
              fontSize: "14px",
              transition: "background-color 0.2s ease"
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = "#f8f9fa"}
            onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
            onClick={() => {
              setUserMenuOpen(false);
              onSectionChange('profile', 'payments');
            }}>Payments</button>
            <div style={{ height: "1px", background: "#eee", margin: "4px 0" }}></div>
            <button 
              onClick={handleLogout}
              style={{
                width: "100%",
                background: "none",
                border: "none",
                padding: "8px 16px",
                textAlign: "left",
                cursor: "pointer",
                fontSize: "14px",
                color: "#dc2626",
                transition: "background-color 0.2s ease"
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#fef2f2"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
            >
              Sign Out
            </button>
          </div>
        </div>
      )}
      
      {tooltip.show && (
        <div
          className="sidebar-tooltip"
          style={{
            position: "fixed",
            left: 100,
            top: tooltip.top,
            transform: "translateY(-50%)",
            background: "rgba(255,255,255,0.7)",
            color: "#222",
            fontWeight: 400,
            fontSize: 12,
            padding: "2.5px 8px",
            borderRadius: 6,
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            zIndex: 9999,
            pointerEvents: "none",
            userSelect: "none",
            whiteSpace: "nowrap",
            opacity: tooltip.show ? 1 : 0,
            transition: "opacity 0.18s cubic-bezier(.4,0,.2,1)"
          }}
        >
          {tooltip.text}
        </div>
      )}
    </>
  );
}
